// src/pages/ContactPage.js
//
// Publieke contactpagina op /contact, met dezelfde inhoud als de contact-modal
// (Strapi single type contact-pagina).

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';

import Header from '../components/Header';
import Footer from '../components/Footer';
import LoginModal from '../components/LoginModal';
import RegistrationModal from '../components/RegistrationModal';
import InfoModal from '../components/InfoModal';
import ContactSocials from '../components/ContactSocials';
import SEO from '../components/SEO';
import { ReactComponent as ChevronUpIcon } from '../assets/icon-chevron-up.svg';
import { toDescription } from '../utils/seoText';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

function ContactPage() {
  const navigate = useNavigate();
  const [contactData, setContactData] = useState(null);
  const [privacyData, setPrivacyData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contactRes, privacyRes] = await Promise.all([
          axios.get(`${STRAPI_URL}/api/contact-pagina?populate=*`),
          axios.get(`${STRAPI_URL}/api/privacyverklaring?populate=*`),
        ]);
        setContactData(contactRes.data.data);
        setPrivacyData(privacyRes.data.data);
      } catch (err) {
        console.error('Fout bij het ophalen van de contactpagina:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const description = toDescription(
    contactData?.body,
    'Neem contact op met Doorloopjes: e-mail, telefoon, adres en bereikbaarheid.'
  );

  return (
    <div className="page-container seo-page">
      <SEO title="Contact | Doorloopjes" description={description} url="/contact" />
      <Header
        onLoginClick={() => setIsLoginModalOpen(true)}
        onRegisterClick={() => setIsRegisterModalOpen(true)}
      />

      {isLoginModalOpen && (
        <LoginModal
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={() => {
            setIsLoginModalOpen(false);
            window.location.href = '/dashboard';
          }}
          onOpenRegister={() => {
            setIsLoginModalOpen(false);
            setIsRegisterModalOpen(true);
          }}
        />
      )}

      {isRegisterModalOpen && (
        <RegistrationModal
          onClose={() => setIsRegisterModalOpen(false)}
          onRegisterSuccess={() => {
            setIsRegisterModalOpen(false);
            window.location.href = '/dashboard';
          }}
        />
      )}

      <InfoModal
        show={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        title={privacyData?.title}
        content={privacyData?.body}
        loading={false}
        showSocial={false}
      />

      <InfoModal
        show={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title={contactData?.title}
        content={contactData?.body}
        loading={false}
        showSocial={true}
      />

      <main>
        <div className="seo-page-content">
          <button className="back-button" onClick={() => navigate('/')}>
            <ChevronUpIcon className="back-icon" />
            <span>Terug</span>
          </button>

          <h1>Contact</h1>

          <div style={{ marginTop: '1.5rem' }}>
            {loading ? (
              <p>Contactgegevens worden geladen...</p>
            ) : contactData?.body ? (
              <BlocksRenderer content={contactData.body} />
            ) : (
              <p>
                De contactgegevens laden lukte niet. Mail ons op{' '}
                <a href="mailto:info@doorloopjes.nl">info@doorloopjes.nl</a>.
              </p>
            )}
          </div>

          <div style={{ marginTop: '2rem' }}>
            <ContactSocials />
          </div>
        </div>
      </main>

      <Footer
        onLoginClick={() => setIsLoginModalOpen(true)}
        onPrivacyClick={() => setIsPrivacyModalOpen(true)}
        onContactClick={() => setIsContactModalOpen(true)}
      />
    </div>
  );
}

export default ContactPage;
